/**
 * File Service Utilities
 *
 * Centralized utilities for file operations including preview and download.
 * Files are stored in AWS S3 and accessed through the backend proxy.
 */

/**
 * Generate the correct URL for file preview
 * @param fileId - The file ID
 * @returns The preview URL that streams the file from backend
 */
export function getFilePreviewUrl(fileId: number | string): string {
  // Use the dedicated file endpoint which handles S3 streaming
  // preview=true sets Content-Disposition to inline for browser display
  return `/api/files/${fileId}/download?preview=true`;
}

/**
 * Generate the correct URL for file download
 * @param fileId - The file ID
 * @returns The download URL that forces browser download
 */
export function getFileDownloadUrl(fileId: number | string): string {
  // Use the dedicated file endpoint which handles S3 streaming
  // preview=false (or omitted) sets Content-Disposition to attachment
  return `/api/files/${fileId}/download?preview=false`;
}

/**
 * Open a file for preview in a new tab
 * @param fileId - The file ID
 */
export function previewFile(fileId: number | string): void {
  const url = getFilePreviewUrl(fileId);
  window.open(url, '_blank');
}

/**
 * Trigger a file download
 * @param fileId - The file ID
 */
export function downloadFile(fileId: number | string): void {
  const url = getFileDownloadUrl(fileId);
  // Open in new tab - the attachment disposition will force download
  window.open(url, '_blank');
}

/**
 * Office document MIME types that preview inline via the download-preview path — the backend converts
 * them to PDF on the fly (`GET /api/files/:id/download?preview=true`). Kept in step with the backend
 * convertible-type set (`backend/src/files/utils/office-document.util.ts`): widening one without the
 * other either hides the capability or promises a preview the backend won't produce.
 */
export const PREVIEWABLE_OFFICE_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/docx', // .docx (loose variant seen on some uploads)
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.oasis.opendocument.text' // .odt
]);

/**
 * Image MIME types a browser renders natively.
 *
 * **An allow-list, not `image/*` — that prefix test is what put a broken image in front of a
 * lawyer.** Several image formats no browser decodes reach a matter as email attachments:
 * TIFF (a scanner's default, and what Outlook was mislabelling as PNG), and the HEIC/HEIF the
 * iPhone camera produces. Offering to display one and failing reads as a corrupt or lost file,
 * when the bytes are fine and the honest answer is the download fallback the drawer already has
 * (VALE-1256). Uploads of those types are transcoded server-side before they are stored; an
 * email attachment is not, because the message is the record.
 *
 * Kept in step with `PREVIEWABLE_EXTENSIONS` below — the two are the same claim keyed two ways,
 * and `file-utils.test.ts` fails if they drift.
 */
export const PREVIEWABLE_IMAGE_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg', // Not a registered type, but what a good deal of mail software emits.
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp',
  'image/avif'
]);

/**
 * Check if a file can be previewed in the browser
 * @param mimeType - The file's MIME type
 * @returns Whether the file can be previewed
 */
export function canPreviewFile(mimeType?: string | null): boolean {
  if (!mimeType) return false;

  // PDFs and browser-renderable images preview inline directly; office documents preview via
  // server-side PDF conversion.
  const normalised = mimeType.toLowerCase();
  return (
    normalised === 'application/pdf' ||
    PREVIEWABLE_IMAGE_MIME_TYPES.has(normalised) ||
    PREVIEWABLE_OFFICE_MIME_TYPES.has(normalised)
  );
}

/**
 * Browser-previewable file extensions (lowercase, no dot) — the extension-keyed sibling of
 * `canPreviewFile`. Includes the office types the backend converts to PDF on the preview path,
 * and excludes the image formats no browser renders for the reason given above.
 */
export const PREVIEWABLE_EXTENSIONS = new Set([
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'bmp',
  'avif',
  // Office documents — the backend converts these to PDF on the download-preview path.
  'docx',
  'doc',
  'xlsx',
  'pptx',
  'odt'
]);

/**
 * Whether a file can be previewed inline in the browser, decided from its extension rather than its
 * MIME type — the sibling of {@link canPreviewFile} for callers that only have the extension (e.g.
 * the grant cheatsheet, where the BFF surfaces each reference document's extension). PDFs, images,
 * and LibreOffice-convertible office documents (`.docx`, `.doc`, `.xlsx`, `.pptx`, `.odt`) preview
 * inline; genuinely unconvertible types (`.msg`, archives) must be downloaded instead.
 *
 * @param extension - The file extension, with or without a leading dot (case-insensitive).
 */
export function isPreviewableExtension(extension?: string | null): boolean {
  if (!extension) return false;
  return PREVIEWABLE_EXTENSIONS.has(extension.replace(/^\./, '').toLowerCase());
}
